import { i18n } from "@/i18n"
import { TagBaseType } from "@/models/etc/types"
import { parseJobDeadline, serializeJobDeadline } from "@/utils/jobDeadline"
import type { CommonEntityRecord } from "@/models/interfaces/common"
import type { TagWithFrequency } from "@/models/interfaces/tags/tag-models"
import { AutoJobAuthorType } from "@/models/interfaces/project/autojob-models"
import type {
    AutoJobAuthorHolder,
    AutoJobAuthorInProject,
    AutoJobAuthorListResponse,
    AutoJobExecutorHolder,
    AutoJobExecutorInProject,
    AutoJobExecutorListResponse,
    AutoJobTemplateFilled,
    AutoJobTemplateInProject,
    AutoJobTemplateInProjectGiver
} from "@/models/interfaces/project/autojob-models"

const t = (key: string): string => String(i18n.global.t(key))

export type AutoJobTemplateDirection = 'from' | 'to'

export type AutoJobTemplateField = 'template' | 'text' | 'author' | 'executors' | 'date' | 'tags'

export type AutoJobTemplateErrors = Partial<Record<AutoJobTemplateField, string>>

export type AutoJobTemplateServerError = {
    field: AutoJobTemplateField
    messageKey: string
}

// Редактируемое состояние одной карточки окна авторабот
export type AutoJobTemplateState = {
    key: string
    direction: AutoJobTemplateDirection
    // Снимок с сервера: держатели и блокировки полей. Заменяется при обновлении списков
    source: AutoJobTemplateInProject
    selected: boolean
    text: string
    authorKey: string | null
    executorKeys: string[]
    needChecking: boolean
    date: Date | null
    // Только редактируемые теги, заблокированные лежат в source.LockedTags
    tags: TagWithFrequency[]
    needRefresh: { authors: boolean, executors: boolean }
    refreshing: { authors: boolean, executors: boolean }
    serverError: AutoJobTemplateServerError | null
}

export type AutoJobTemplatePatch = Partial<Pick<AutoJobTemplateState,
    'selected' | 'text' | 'authorKey' | 'executorKeys' | 'needChecking' | 'date' | 'tags'>>

// Опции для AppSelect
export type AutoJobOption = {
    id: string
    label: string
    type: 'item'
    disabled?: boolean
    description?: string
}

export type AutoJobGroupOption = {
    id: string
    label: string
    type: 'group'
    selectable: boolean
    children: AutoJobOption[]
}

export type AutoJobSelectOption = AutoJobOption | AutoJobGroupOption

// Исполнитель из плоского списка: сотрудники внутри отделов всегда считаются сотрудниками
type ExecutorEntry = {
    key: string
    executor: AutoJobExecutorInProject
    isWorker: boolean
}

// У ролей и сотрудников общее пространство Id, у «особых» поручителей (меняющий этап,
// создатель проекта) Id может не быть — поэтому ключи выбора составные
export const getAuthorKey = (author: AutoJobAuthorInProject): string =>
    `author:${author.Type}:${author.Id ?? 0}`

const getExecutorKey = (executor: AutoJobExecutorInProject, isWorker: boolean): string =>
    `${isWorker ? 'worker' : 'role'}:${executor.Id}`

const collectAuthors = (holders: AutoJobAuthorHolder[] | null | undefined): AutoJobAuthorInProject[] => {
    const result: AutoJobAuthorInProject[] = []
    for (const holder of holders ?? []) {
        if (holder.IsAuthor) {
            if (holder.Author) {
                result.push(holder.Author)
            }
            continue
        }
        result.push(...(holder.Department?.Authors ?? []))
    }
    return result
}

const collectExecutors = (holders: AutoJobExecutorHolder[] | null | undefined): ExecutorEntry[] => {
    const result: ExecutorEntry[] = []
    for (const holder of holders ?? []) {
        if (holder.IsExecutor) {
            if (holder.Executor) {
                const isWorker = holder.Executor.IsWorker
                result.push({ key: getExecutorKey(holder.Executor, isWorker), executor: holder.Executor, isWorker })
            }
            continue
        }
        for (const executor of holder.Department?.Executors ?? []) {
            result.push({ key: getExecutorKey(executor, true), executor, isWorker: true })
        }
    }
    return result
}

const findAuthor = (
    holders: AutoJobAuthorHolder[] | null | undefined,
    key: string | null
): AutoJobAuthorInProject | null => {
    if (!key) {
        return null
    }
    return collectAuthors(holders).find(author => getAuthorKey(author) === key) ?? null
}

const getSelectedExecutors = (holders: AutoJobExecutorHolder[] | null | undefined, keys: string[]): ExecutorEntry[] => {
    const seen = new Set<string>()
    return collectExecutors(holders).filter(entry => {
        if (!keys.includes(entry.key) || seen.has(entry.key)) {
            return false
        }
        seen.add(entry.key)
        return true
    })
}

// Выбор поручителя, который предлагает сервер. Если единственный вариант — роль, её
// выбираем сразу: так видна ошибка «в роли нет пользователей»
const getServerAuthorKey = (holders: AutoJobAuthorHolder[] | null | undefined): string | null => {
    const selected = collectAuthors(holders).find(author => author.Selected)
    if (selected) {
        return getAuthorKey(selected)
    }

    const list = holders ?? []
    const single = list.length === 1 && list[0].IsAuthor ? list[0].Author : null
    if (single && single.Type === AutoJobAuthorType.Role) {
        return getAuthorKey(single)
    }

    return null
}

const getServerExecutorKeys = (holders: AutoJobExecutorHolder[] | null | undefined): string[] => {
    return Array.from(new Set(
        collectExecutors(holders)
            .filter(entry => entry.executor.Selected)
            .map(entry => entry.key)
    ))
}

const getAuthorLabel = (author: AutoJobAuthorInProject): string => {
    if (author.Type === AutoJobAuthorType.Role) {
        return `#${author.Name}`
    }
    if (author.Name) {
        return author.Name
    }
    if (author.Type === AutoJobAuthorType.StageChanger) {
        return t('autojob_author_stage_changer')
    }
    if (author.Type === AutoJobAuthorType.ProjectCreator) {
        return t('autojob_author_project_creator')
    }
    return ''
}

const getExecutorLabel = (entry: ExecutorEntry): string =>
    entry.isWorker ? entry.executor.Name : `#${entry.executor.Name}`

const isEmptyRole = (entry: ExecutorEntry): boolean =>
    !entry.isWorker && !entry.executor.Workers?.length

// Почему исполнителя нельзя выбрать: у роли нет участников в проекте или у сотрудника нет доступа
const getExecutorProblem = (entry: ExecutorEntry): string => {
    if (isEmptyRole(entry)) {
        return t('autojob_error_role_empty')
    }
    if (entry.executor.HasAccess === false) {
        return t('autojob_worker_no_access')
    }
    return ''
}

const toAuthorOption = (author: AutoJobAuthorInProject): AutoJobOption => {
    const isRole = author.Type === AutoJobAuthorType.Role
    return {
        id: getAuthorKey(author),
        label: getAuthorLabel(author),
        type: 'item',
        disabled: isRole,
        description: isRole ? t('autojob_error_role_empty') : undefined
    }
}

// Роль в поручителях — только маркер «нет подходящих пользователей», выбрать её нельзя.
// Показываем её, только если она выбрана: так видна ошибка
export function buildAuthorOptions(
    holders: AutoJobAuthorHolder[] | null | undefined,
    selectedKey: string | null
): AutoJobSelectOption[] {
    const isVisible = (author: AutoJobAuthorInProject): boolean =>
        author.Type !== AutoJobAuthorType.Role || getAuthorKey(author) === selectedKey

    const result: AutoJobSelectOption[] = []
    const list = holders ?? []
    list.forEach((holder, index) => {
        if (holder.IsAuthor) {
            if (holder.Author && isVisible(holder.Author)) {
                result.push(toAuthorOption(holder.Author))
            }
            return
        }
        if (holder.Department) {
            const children = (holder.Department.Authors ?? [])
                .filter(isVisible)
                .map(toAuthorOption)
            if (children.length) {
                result.push({
                    id: `author-dep:${index}`,
                    label: holder.Department.Name,
                    type: 'group',
                    selectable: false,
                    children
                })
            }
        }
    })
    return result
}

// Исполнителей без доступа к проекту и роли без участников не показываем, пока они не выбраны;
// проблемного выбранного можно снять
export function buildExecutorOptions(
    holders: AutoJobExecutorHolder[] | null | undefined,
    selectedKeys: string[]
): AutoJobSelectOption[] {
    const isVisible = (entry: ExecutorEntry): boolean =>
        selectedKeys.includes(entry.key) || (entry.executor.HasAccess !== false && !isEmptyRole(entry))

    const toOption = (entry: ExecutorEntry): AutoJobOption => {
        const problem = getExecutorProblem(entry)
        return {
            id: entry.key,
            label: getExecutorLabel(entry),
            type: 'item',
            disabled: !!problem && !selectedKeys.includes(entry.key),
            description: problem || undefined
        }
    }

    const result: AutoJobSelectOption[] = []
    const list = holders ?? []
    list.forEach((holder, index) => {
        if (holder.IsExecutor) {
            if (holder.Executor) {
                const isWorker = holder.Executor.IsWorker
                const entry = { key: getExecutorKey(holder.Executor, isWorker), executor: holder.Executor, isWorker }
                if (isVisible(entry)) {
                    result.push(toOption(entry))
                }
            }
            return
        }
        if (holder.Department) {
            const children = (holder.Department.Executors ?? [])
                .map(executor => ({ key: getExecutorKey(executor, true), executor, isWorker: true }))
                .filter(isVisible)
                .map(toOption)
            if (children.length) {
                result.push({
                    id: `executor-dep:${index}`,
                    label: holder.Department.Name,
                    type: 'group',
                    selectable: true,
                    children
                })
            }
        }
    })
    return result
}

export function buildExecutorSummary(holders: AutoJobExecutorHolder[] | null | undefined, keys: string[]): string {
    return getSelectedExecutors(holders, keys).map(getExecutorLabel).join(', ')
}

export const toTagWithFrequency = (tag: CommonEntityRecord): TagWithFrequency => ({
    Id: tag.Id,
    Name: tag.Name,
    Type: TagBaseType.Common,
    Frequency: 0,
    FullName: tag.Name
})

export const parseAutoJobDate = (value: string | null | undefined): Date | null =>
    parseJobDeadline(value)

export const serializeAutoJobDate = (date: Date | null): string | null =>
    date ? serializeJobDeadline(date) : null

export const formatAutoJobDate = (date: Date | null): string => {
    if (!date) {
        return ''
    }
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = String(date.getFullYear()).slice(-2)
    return `${day}.${month}.${year}`
}

export function createTemplateState(
    source: AutoJobTemplateInProject,
    direction: AutoJobTemplateDirection
): AutoJobTemplateState {
    return {
        key: `${direction}:${source.Id}`,
        direction,
        source,
        selected: !!source.Selected || !!source.IsMandatory,
        text: source.Text ?? '',
        authorKey: getServerAuthorKey(source.AuthorHolders),
        executorKeys: getServerExecutorKeys(source.ExectutorHolders),
        needChecking: !!source.NeedChecking,
        date: parseAutoJobDate(source.Date),
        tags: (source.Tags ?? []).map(toTagWithFrequency),
        needRefresh: { authors: false, executors: false },
        refreshing: { authors: false, executors: false },
        serverError: null
    }
}

// Текущий выбор сохраняется, если он ещё есть в новом списке,
// иначе — и для заблокированного поля — берётся то, что предлагает сервер
export function applyAuthorsRefresh(state: AutoJobTemplateState, giver: AutoJobAuthorListResponse): AutoJobTemplateState {
    const holders = giver.AuthorHolders ?? []
    const keepCurrent = !giver.AuthorLocked && !!findAuthor(holders, state.authorKey)
    return {
        ...state,
        source: {
            ...state.source,
            AuthorHolders: holders,
            AuthorLocked: giver.AuthorLocked,
            AuthorIsValid: giver.AuthorIsValid
        },
        authorKey: keepCurrent ? state.authorKey : getServerAuthorKey(holders),
        needRefresh: { ...state.needRefresh, authors: false }
    }
}

export function applyExecutorsRefresh(state: AutoJobTemplateState, giver: AutoJobExecutorListResponse): AutoJobTemplateState {
    const holders = giver.ExectutorHolders ?? []
    const kept = giver.ExecutorLocked
        ? []
        : getSelectedExecutors(holders, state.executorKeys).map(entry => entry.key)
    return {
        ...state,
        source: {
            ...state.source,
            ExectutorHolders: holders,
            ExecutorLocked: giver.ExecutorLocked
        },
        executorKeys: kept.length ? kept : getServerExecutorKeys(holders),
        needRefresh: { ...state.needRefresh, executors: false }
    }
}

// Правило против дублей: добавили роль — из выбора уходят её
// участники, добавили сотрудника (в том числе отметкой отдела) — уходят роли, куда он входит
export function applyExecutorChange(
    prevKeys: string[],
    nextKeys: string[],
    holders: AutoJobExecutorHolder[] | null | undefined
): string[] {
    const added = nextKeys.filter(key => !prevKeys.includes(key))
    if (!added.length) {
        return nextKeys
    }

    const entries = collectExecutors(holders)
    const byKey = new Map(entries.map(entry => [entry.key, entry]))
    let result = [...nextKeys]

    const addedRoles = entries.filter(entry => !entry.isWorker && added.includes(entry.key))
    addedRoles.forEach(role => {
        const memberIds = new Set((role.executor.Workers ?? []).map(worker => worker.Id))
        result = result.filter(key => {
            const entry = byKey.get(key)
            return !(entry && entry.isWorker && memberIds.has(entry.executor.Id))
        })
    })

    const addedWorkerIds = new Set(
        result
            .filter(key => added.includes(key))
            .map(key => byKey.get(key))
            .filter((entry): entry is ExecutorEntry => !!entry && entry.isWorker)
            .map(entry => entry.executor.Id)
    )
    if (addedWorkerIds.size) {
        result = result.filter(key => {
            const entry = byKey.get(key)
            if (!entry || entry.isWorker) {
                return true
            }
            return !(entry.executor.Workers ?? []).some(worker => addedWorkerIds.has(worker.Id))
        })
    }

    return result
}

const startOfDay = (date: Date): number => {
    const copy = new Date(date)
    copy.setHours(0, 0, 0, 0)
    return copy.getTime()
}

// Проверка заполнения. AuthorIsValid с сервера не используется
export function validateTemplateState(state: AutoJobTemplateState, today: Date = new Date()): AutoJobTemplateErrors {
    const errors: AutoJobTemplateErrors = {}

    if (!state.text.trim()) {
        errors.text = 'autojob_error_text_empty'
    }

    const author = findAuthor(state.source.AuthorHolders, state.authorKey)
    if (!author) {
        errors.author = 'autojob_error_author_empty'
    } else if (author.Type === AutoJobAuthorType.Role) {
        errors.author = 'autojob_error_role_empty'
    }

    const executors = getSelectedExecutors(state.source.ExectutorHolders, state.executorKeys)
    const roles = executors.filter(entry => !entry.isWorker)
    if (!executors.length) {
        errors.executors = 'autojob_error_executor_empty'
    } else if (executors.some(entry => entry.executor.HasAccess === false)) {
        errors.executors = 'autojob_error_no_access'
    } else if (roles.length && roles.every(role => !role.executor.Workers?.length)) {
        errors.executors = 'autojob_error_role_empty'
    }

    if (!state.date) {
        errors.date = 'autojob_error_date_empty'
    } else if (startOfDay(state.date) < startOfDay(today)) {
        errors.date = 'autojob_error_date_invalid'
    }

    return errors
}

export const hasTemplateErrors = (errors: AutoJobTemplateErrors): boolean =>
    Object.keys(errors).length > 0

const mergeTags = (locked: CommonEntityRecord[] | null | undefined, tags: TagWithFrequency[]): CommonEntityRecord[] => {
    const seen = new Set<string>()
    const result: CommonEntityRecord[] = []
    const push = (tag: CommonEntityRecord) => {
        const name = tag.Name.trim().toLowerCase()
        if (!name || seen.has(name)) {
            return
        }
        seen.add(name)
        // У тегов, созданных в окне, временный отрицательный Id — сервер создаёт их по имени
        result.push({ Id: tag.Id > 0 ? tag.Id : 0, Name: tag.Name })
    }
    const lockedTags = locked ?? []
    lockedTags.forEach(push)
    tags.forEach(push)
    return result
}

const buildFilledTemplate = (state: AutoJobTemplateState): AutoJobTemplateFilled => {
    const author = findAuthor(state.source.AuthorHolders, state.authorKey)
    const executors = getSelectedExecutors(state.source.ExectutorHolders, state.executorKeys)
    return {
        Id: state.source.Id,
        Text: state.text,
        // Без поручителя окно отправку не пустит; 0 — «не выбран»
        AuthorType: author ? author.Type : 0,
        AuthorId: author && author.Type === AutoJobAuthorType.Worker ? author.Id : null,
        ExecutorRoles: executors.filter(entry => !entry.isWorker).map(entry => entry.executor.Id),
        ExecutorWorkers: executors.filter(entry => entry.isWorker).map(entry => entry.executor.Id),
        NeedChecking: state.needChecking,
        Date: serializeAutoJobDate(state.date),
        Tags: mergeTags(state.source.LockedTags, state.tags)
    }
}

// Порядок: сначала шаблоны перевода на этап, потом снятия с этапа
export function buildFilledTemplates(states: AutoJobTemplateState[]): AutoJobTemplateFilled[] {
    const selected = states.filter(state => state.selected)
    return [
        ...selected.filter(state => state.direction === 'to'),
        ...selected.filter(state => state.direction === 'from')
    ].map(buildFilledTemplate)
}

export function isAutoJobsRequired(
    response: AutoJobTemplateInProjectGiver | null | undefined
): response is AutoJobTemplateInProjectGiver {
    if (!response) {
        return false
    }
    return !!(response.TemplatesFrom?.length || response.TemplatesTo?.length)
}
